import { makeStyles, Typography, TypographyProps } from '@material-ui/core';
import clsx from 'clsx';
import React, { CSSProperties, ElementType, Fragment, useCallback, useMemo } from 'react';
import { EntityUIComponentCollection, EntityUIComponentProps } from '..';
import { useRitzTranslation } from '../../../utils/customHooks';
import { useSelectEntity, useSelectProject } from '../../../utils/dataStateHooks';
import { SettingsErrorLogger } from '../../../utils/logger';
import { RitzComponentsCollection } from '../../../utils/ritzComponentsCollection';
import { isObjectLiteral, isValidSettingBooleanValue, isValidSettingStringValue } from '../../../utils/utils';


const useStyle = makeStyles(() => {
    return {
        root: {
            '&.clickable': {
                cursor: 'pointer',
            },
        },
    };
}, { name: 'EntityHeadingUIComponent', index: 1 });

/**
 * Interface for EntityHeaderUIComponentCustomSettings's customSettings. These are the props/value that can be
 * configured for the 'EntityHeaderUIComponentCustomSettings' from the entityClass's uiSettings.
 */
export interface EntityHeadingUIComponentCustomSettings {
    // The string to be used for displaying in the heading. This can be a translation key or a fixed string. We will try to look up for a value
    // for this key from the project's translation override and display that value if we find one. If not, the key will be displayed as is.
    // NOTE: the value for this can contain placeholders. The placeholders will be replace with the actual values. The placeholders that this
    // component support is the entity's "name", "description", and all the entity's attributes. For example, to show the entity's name and the
    // attribute "cardId" in the title, we can do this
    //      titleKey: "Driver's {{name}} with ID {{cardId}} Daily Breathalyzer Test Result"
    readonly titleKey: string;

    // Whether or not the heading is clickable and should take the user to the entity details page that is associated with this component
    readonly clickable?: boolean | undefined;

    // The custom CSS style for the outer container element
    readonly containerStyle?: CSSProperties | undefined;

    // To specify the HTML Element type to use for the element e.g. "h1", "h2", "div", "span", "li", etc...
    readonly component?: ElementType<any> | undefined;

    // To specify one of the predefined variants to use. These the the supported variants
    // 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'subtitle1', 'subtitle2', 'body1', 'body2', 'caption', 'button', 'overline';
    readonly variant?: TypographyProps['variant'] | undefined;
}



/**
 * type guard function to check if an object is a type of EntityHeaderUIComponentCustomSettings
 */
const isEntityHeadingUIComponentCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityHeadingUIComponentCustomSettings => {

    const settings = obj as EntityHeadingUIComponentCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.titleKey, true)) {
        return errorLogger?.logError({
            message: '"titleKey" must be a non-empty string.',
            value  : settings.titleKey,
            path   : [...configPath, 'titleKey'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.clickable, false)) {
        return errorLogger?.logError({
            message: '"clickable" must be undefined or a boolean.',
            value  : settings.clickable,
            path   : [...configPath, 'clickable'],
        }) ?? false;
    }

    if (settings.containerStyle !== undefined && !isObjectLiteral(settings.containerStyle)) {
        return errorLogger?.logError({
            message: '"containerStyle" must be undefined or a CSSProperties object.',
            value  : settings.containerStyle,
            path   : [...configPath, 'containerStyle'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.component)) {
        return errorLogger?.logError({
            message: '"component" must be undefined or a non-empty string.',
            value  : settings.component,
            path   : [...configPath, 'component'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.variant)) {
        return errorLogger?.logError({
            message: '"variant" must be undefined or a non-empty string.',
            value  : settings.variant,
            path   : [...configPath, 'variant'],
        }) ?? false;
    }

    return true;
};



export interface EntityHeadingUIComponentProps extends EntityUIComponentProps {
    readonly className?: string | undefined;
    readonly customSettings?: EntityHeadingUIComponentCustomSettings | undefined;
}


/**
 * This is a UI Component that can be used as a container for other components. It does not display entity data but only Entity UI Components.
 */
export const EntityHeadingUIComponent: React.FC<EntityHeadingUIComponentProps> = (
    props: EntityHeadingUIComponentProps,
) => {
    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const selectedEntity = useSelectEntity(props.projectId, props.entityId);
    const classes = useStyle();

    /**
     * Create an object containing some of the entity's data that can be used as placeholder. We will add the entity's name, description, and
     * everything from the entity's attributes.
     */
    const placeholderValues = useMemo(() => {
        if (selectedEntity) {
            return {
                name       : selectedEntity.displaySettings.name,
                description: selectedEntity.displaySettings.description,
                ...selectedEntity.attributes,                               // Add all the name/value pairs from the entity's attributes
            };
        }
        return undefined;
    }, [selectedEntity]);


    const onClick = useCallback((event) => {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }
        if (props.onShowEntityDetails) {
            props.onShowEntityDetails(props.entityId);
        }
    }, [props]);

    return (
        <>
            {props.customSettings?.titleKey && (
                <div
                    className={clsx(classes.root, props.customSettings.clickable && 'clickable')}
                    onClick={props.customSettings.clickable ? onClick : undefined}
                >
                    <Typography
                        variant={props.customSettings.variant ?? 'h5'}
                        component={props.customSettings.component ?? 'h3'}
                        style={props.customSettings.containerStyle}
                    >
                        {trans(props.customSettings.titleKey, placeholderValues)}
                    </Typography>
                </div>
            )}
        </>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent(
    'EntityHeadingUIComponent', EntityHeadingUIComponent,
);



// Register the component with the EntityUIComponentCollection so that the component can be used by the Entity Layout System
EntityUIComponentCollection.getInstance().registerComponent(
    'Entity_Heading_Comp',
    {
        componentFunc          : EntityHeadingUIComponent,
        customSettingsValidator: isEntityHeadingUIComponentCustomSettings,
    },
);
